import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DealerRegisterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '', confirm_password: '', pin_code: '', address: '' });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetch(`${API}/dealer/invite/${token}`)
      .then(async res => {
        if (res.ok) {
          setInvitation(await res.json());
        } else {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || 'Ugyldig eller utløpt invitasjon');
        }
      })
      .catch(() => setError('Kunne ikke koble til serveren'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.username.trim()) {
      setFormError('Brukernavn er påkrevd'); return;
    }
    if (formData.password.length < 4) {
      setFormError('Passord må være minst 4 tegn'); return;
    }
    if (formData.password !== formData.confirm_password) {
      setFormError('Passordene stemmer ikke overens'); return;
    }
    if (!/^\d{4}$/.test(formData.pin_code)) {
      setFormError('PIN-kode må være 4 siffer'); return;
    }
    if (!formData.address.trim()) {
      setFormError('Postadresse er påkrevd for sending av varer'); return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/dealer/complete-registration/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          pin_code: formData.pin_code,
          address: formData.address,
        }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const err = await res.json().catch(() => ({}));
        setFormError(err.detail || 'Kunne ikke fullføre registreringen. Prøv igjen.');
      }
    } catch {
      setFormError('Kunne ikke koble til serveren. Sjekk internettforbindelsen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 max-w-md w-full text-center" data-testid="invite-error">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="text-red-600" size={48} />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Invitasjonen er ugyldig
          </h2>
          <p className="text-stone-600 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="bg-emerald-800 text-white px-6 py-3 rounded-lg font-medium hover:bg-emerald-900 transition-colors">
            Gå til forsiden
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 max-w-md w-full text-center" data-testid="registration-success">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-600" size={48} />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Registrering fullført!
          </h2>
          <p className="text-stone-600 mb-2">Velkommen som forhandler, <strong>{invitation?.company_name}</strong>!</p>
          <p className="text-stone-500 text-sm mb-6">Du kan nå logge inn i forhandlerportalen med brukernavnet og passordet du valgte.</p>
          <button
            onClick={() => navigate('/forhandler')}
            className="bg-emerald-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-emerald-900 transition-colors"
            data-testid="go-to-login-btn"
          >
            Gå til innlogging
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-12">
        <div className="max-w-lg mx-auto px-4 text-center">
          <Shield className="mx-auto mb-4 text-emerald-300" size={40} />
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="register-page-title">
            Fullfør registrering
          </h1>
          <p className="text-emerald-100">Forhandlerportalen - Gravsteinoppfriskeren</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="register-form-container">
          {/* Company info */}
          <div className="bg-emerald-50 rounded-xl p-4 mb-6 border border-emerald-100">
            <p className="text-sm text-emerald-800 font-medium mb-1">Registrerer for:</p>
            <p className="text-lg font-semibold text-stone-900">{invitation?.company_name}</p>
            <p className="text-sm text-stone-600">{invitation?.contact_person} | {invitation?.email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Brukernavn *</label>
              <input
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Velg et brukernavn for innlogging"
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                data-testid="username-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Velg passord *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={4}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minst 4 tegn"
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent pr-12"
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Bekreft passord *</label>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                placeholder="Skriv passordet på nytt"
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                data-testid="confirm-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Velg PIN-kode (4 siffer) *</label>
              <input
                type="text"
                required
                maxLength={4}
                inputMode="numeric"
                pattern="[0-9]{4}"
                value={formData.pin_code}
                onChange={(e) => setFormData({ ...formData, pin_code: e.target.value.replace(/\D/g, '') })}
                placeholder="4 siffer"
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-center text-2xl tracking-[0.5em]"
                data-testid="pin-input"
              />
              <p className="text-xs text-stone-400 mt-1">PIN-koden brukes for å bekrefte handlinger i portalen</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Postadresse (for varesending) *</label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Gateadresse, postnummer og sted"
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                data-testid="address-input"
              />
            </div>

            {formError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="form-error">
                <AlertTriangle size={18} className="text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-800 text-white py-3 rounded-lg font-medium hover:bg-emerald-900 transition-colors disabled:opacity-50 text-lg"
              data-testid="complete-registration-btn"
            >
              {submitting ? 'Registrerer...' : 'Fullfør registrering'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DealerRegisterPage;
